import { LOCAL_SPACE_X, LOCAL_SPACE_Y } from '@library/constants'
import { glMatrix, mat3, vec2 } from 'gl-matrix'

import { assert } from '../guards'
import type {
  ICanvasSize,
  MapHeader,
  Point2D,
  Point3D,
  PolygonPath,
  Range2D,
  ScaleType,
  Size,
} from '../types'
import { pairwise } from './common'
import {
  applyMatToVec2,
  getCoordsToCanvasMatrix,
  getGridCoordinatesToProjectionMatrix,
} from './matrices'

/**
 * Используется следующие системы координат:
 * 1. GlobalGeological - мировая (глобальная, реальная)
 * 2. local - локальная система координат
 * у локальной системы координат начало совпадает с левым верхним углом прямоугольника
 * ось Y направлена вниз
 * ось X направлена вправо
 * координаты нормированы относительно ширины/высоты прямоугольника и умножены на (LOCAL_SPACE_X, LOCAL_SPACE_Y)
 * 3. canvas - область отрисовки измеряемая в пикселях
 * 5. gridLocal - координаты внутри карты в локальной системе координат с учетом угла поворота
 * 4. gridIndex - индексы в ячейках карты типа Float32Array. Размер соотвестует полям columns и rows
 * у типа MapHeader.
 */

/**
 * Конвертирует реальные координаты в локальные
 *
 * @param {Range2D} origin - параметры преобразования координат: точка отсчета и максимальные размеры
 * @param {Point2D} point - точка в реальных координатах, которую надо перевести
 * @returns {Point2D} точка, в локальной системе координат
 */
export const convertGlobalGeologicalCoordinatesToLocal = (
  origin: Range2D,
  point: Point2D,
): Point2D => {
  const { width: x, height: y } = convertGlobalGeologicalMeasureToLocal(origin, {
    width: point.x - origin.x,
    height: -point.y + origin.y,
  })
  return { x, y }
}

/**
 * Конвертирует реальный размер в локальный
 *
 * @param {Range2D} origin - параметры преобразования координат: точка отсчета и максимальные размеры
 * @param {Size} measure - измеренный размер
 * @returns {Size} размер, в локальной системе координат
 */
export const convertGlobalGeologicalMeasureToLocal = (origin: Range2D, measure: Size): Size => {
  const aspectRatio = origin.height / origin.width
  return {
    width: (measure.width / origin.width) * LOCAL_SPACE_X,
    height: (measure.height / origin.height) * LOCAL_SPACE_Y * aspectRatio,
  }
}

/**
 * Конвертирует локальные координаты в реальные
 *
 * @param {Range2D} origin - параметры преобразования координат: точка отсчета и максимальные размеры
 * @param {Point2D} point - точка в локальных координатах, которую надо перевести
 * @returns {Point2D} точка, в глобальной системе координат
 */
export const convertLocalCoordinatesToGlobalGeological = (
  origin: Range2D,
  point: Point2D,
): Point2D => {
  const size = convertLocalMeasureToGlobalGeological(origin, {
    width: point.x,
    height: point.y,
  })
  return {
    x: origin.x + size.width,
    y: origin.y - size.height,
  }
}

/**
 * Конвертирует локальный размер в реальный
 *
 * @param {Range2D} origin - параметры преобразования координат: точка отсчета и максимальные размеры
 * @param {Size} measure - измеренный размер
 * @returns {Size} размер, в глобальной системе координат
 */
export const convertLocalMeasureToGlobalGeological = (origin: Range2D, measure: Size): Size => {
  const aspectRatio = origin.width / origin.height
  return {
    width: (measure.width / LOCAL_SPACE_X) * origin.width,
    height: (measure.height / LOCAL_SPACE_Y) * origin.height * aspectRatio,
  }
}

const getInvertYMatrix = (sourceMatrix: mat3, height: number) => {
  const mat = mat3.translate(mat3.create(), sourceMatrix, [0, height])
  return mat3.scale(mat3.create(), mat, [1, -1])
}

/**
 * Конвертирует из координат canvas в локальные координаты
 * @param canvas область, в которой выполняется поиск {@link canvasPoint}
 * @param zoom параметры масштабирования на {@link canvas}'е
 * @param canvasPoint точка в координатах {@link canvas}
 * @returns локальные координаты
 */
export const convertCanvasCoordinatesToLocal = (
  canvas: ICanvasSize,
  zoom: ScaleType,
  canvasPoint: Point2D,
): Point2D => {
  const mat = getCoordsToCanvasMatrix(mat3.create(), canvas, zoom)
  const invertMat = mat3.invert(mat3.create(), mat)
  return applyMatToVec2(invertMat, canvasPoint)
}

/**
 * Конвертирует из координат canvas в локальные координаты карты
 * @param canvas область, в которой выполняется поиск {@link canvasPoint}
 * @param zoom параметры масштабирования на {@link canvas}'е
 * @param rotate угол вращения в радианах для карты
 * @param offset точка, относительно которой выполняется вращение карты. Так же является точкой отсчета для возвращаемых координат
 * @param canvasPoint точка в координатах {@link canvas}
 * @returns координаты внутри карты
 */
const convertCanvasCoordinatesToGridLocal = (
  canvas: ICanvasSize,
  zoom: ScaleType,
  rotate: number,
  offset: Point2D,
  canvasPoint: Point2D,
): Point2D => {
  const mat = getGridCoordinatesToProjectionMatrix(canvas, zoom, rotate, offset)
  const invertMat = mat3.invert(mat3.create(), mat)
  const newCoords = vec2.transformMat3(
    vec2.create(),
    vec2.fromValues(
      (canvasPoint.x / canvas.width) * 2 - 1,
      (canvasPoint.y / canvas.height) * 2 - 1,
    ),
    invertMat,
  )

  return { x: newCoords[0], y: newCoords[1] }
}

const convertLocalGridCoordinatesToGridIndex = (point: Point2D, localStep: Size): Point2D => ({
  x: point.x / localStep.width,
  y: -point.y / localStep.height,
})

/**
 * Конвертирует из координат canvas в индексы карты
 * @param canvas область, в которой выполняется поиск {@link canvasPoint}
 * @param mapHeader параметры карты
 * @param zoom параметры масштабирования на {@link canvas}'е
 * @param origin границы рабочей области
 * @param canvasPoint точка в координатах {@link canvas}
 * @returns индекс карты для {@link canvasPoint}
 */
export const convertCanvasCoordinatesToGridIndex = (
  canvas: ICanvasSize,
  mapHeader: MapHeader,
  zoom: ScaleType,
  origin: Range2D,
  canvasPoint: Point2D,
) => {
  const localRotationPoint = convertGlobalGeologicalCoordinatesToLocal(origin, {
    x: mapHeader.rotationX,
    y: mapHeader.rotationY,
  })

  const localGridPoint = convertCanvasCoordinatesToGridLocal(
    canvas,
    zoom,
    glMatrix.toRadian(mapHeader.rotationAngle),
    localRotationPoint,
    canvasPoint,
  )

  const localStep = convertGlobalGeologicalMeasureToLocal(origin, {
    width: mapHeader.stepX,
    height: mapHeader.stepY,
  })

  return convertLocalGridCoordinatesToGridIndex(localGridPoint, localStep)
}

export const calculatePolygonPath = (pointsPath: Point3D[], isClosed: boolean): PolygonPath[] => {
  const polygonPath = pairwise(pointsPath, (pointFrom, pointTo) => ({
    from: pointFrom,
    to: pointTo,
    z: (pointFrom.z + pointTo.z) / 2,
  }))

  if (isClosed) {
    const first = pointsPath.at(0)
    const last = pointsPath.at(-1)

    assert(first != null)
    assert(last != null)

    polygonPath.push({ from: last, to: first, z: (first.z + last.z) / 2 })
  }

  return polygonPath
}
